import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useBlocker } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import {
  AiGeneratedOutput,
  AttachedTest,
  BlockResponse,
  capitalize,
  cn,
  ControlCode,
  ControlCodes,
  Detection,
  DetectionReport,
  displayPlatform,
  ExitCodeGroup,
  formatDate,
  formatPercent,
  getControlName,
  getStatusBorderColor,
  getStatusColor,
  isAdminUser,
  parseDate,
  PartnerExpected,
  queries,
  Test,
  TestsActivity,
  ThreatHunt,
  TimeFormats,
  useAccountQuery,
  useAuthConfig,
  useGenerateTechnique,
} from "@theprelude/core";
import {
  AlertIcon,
  Button,
  CalendarIcon,
  CheckmarkIcon,
  CloseIcon,
  CodeIcon,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ControlIcon,
  CopyButton,
  DetectionIcon,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DownloadIcon,
  Dropdown,
  DropdownContent,
  DropdownRadio,
  DropdownRadioGroup,
  DropdownTrigger,
  EyeIcon,
  IconButton,
  InfoIcon,
  InputField,
  notifyError,
  notifySuccess,
  OverlayInfoBlock,
  ProgressBar,
  RefreshIcon,
  ResponseIcon,
  RotatingChevron,
  SearchIcon,
  ShieldFlash,
  SignalIcon,
  TargetIcon,
  ToggleGroup,
  ToggleItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useCollapsible,
  useDownloadFile,
  UserIcon,
  PlatformIcon,
  VSTIndicatorIcon,
} from "@theprelude/ds";
import { Suspense, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import ReactMarkdown from "react-markdown";
import { usePartnerBlockMutationWithToast } from "@/hooks/partner-mutations";
import { useOverlay } from "@/hooks/use-overlay";
import { usePopper } from "@/hooks/use-popper";
import { getExpectedName, getExpectedPercent } from "@/lib/odp";
import { isValidControl } from "@/lib/validators";
import {
  TestOverlayData,
  TestResult,
  ThreatActivityEntry,
} from "@/queries/tests";
import { DeepLinkActionsColumn } from "../deep-links";
import Editor from "../editor/editor";
import { ExternalLink } from "../external-link";
import { Flag, hasFeatures } from "../flag/flag";
import {
  BenchmarkPercent,
  NonApplicablePercent,
  ProtectedPercent,
} from "../protection-blocks/protection-blocks";
import {
  authorColumn,
  detectedResultColumn,
  edrColumn,
  expectedOutcomeColumn,
  hostSmallColumn,
  observedResultColumn,
  osColumn,
  preventedStatusColumn,
  protectedColumn,
  sourceIdColumn,
  threatIdColumn,
  threatNameSmallColumn,
  timeColumn,
} from "../table/columns";
import { TableFilterInput } from "../table/filter-input";
import { useTable } from "../table/hooks";
import { VirtualTable } from "../table/virtual-table";
import {
  formatEditableDetectionsFromAI,
  formatEditableThreatHuntsFromAI,
  TestEditorField,
} from "../test-editor/controlled-test-editor";
import {
  ViewDetections,
  ViewThreatHunts,
} from "../test-editor/editor-components";
import ThreatImage from "../threat-intel/threat-image";
import styles from "./test-overlay.module.css";
import { UpdateTestForm, useUpdateRegeneratedTest } from "./use-update-test";

const getTabs = (test: AttachedTest) => {
  const tabs = [];

  if (test.attachments.includes("README.md")) {
    tabs.push("description");
  }

  if (test.attachments.includes(`${test.id}.go`)) {
    tabs.push("sourceCode");
  }

  return tabs;
};

const getDetectionStatus = (block?: BlockResponse) => {
  let status: "created" | "error" | "already_exists" | undefined = undefined;
  let error: string | undefined = undefined;
  let created: string | undefined = undefined;

  if (!block) return;

  const rules = block.rules;

  if (rules.some((rule) => rule.status === "ERROR")) {
    status = "error";
    error = rules.find((rule) => rule.status === "ERROR")?.error;
  }

  if (rules.every((rule) => rule.status === "CREATED")) {
    status = "created";
    created = rules[0]?.created;
  }

  if (rules.every((rule) => rule.status === "ALREADY_EXISTS")) {
    status = "already_exists";
    created = rules[0]?.created;
  }

  return { status, error, created };
};

const platforms = ["windows", "linux", "macos"] as const;

function EDRDetections({
  results,
  reports,
  platforms,
}: {
  results?: BlockResponse[];
  reports: DetectionReport[];
  platforms: ("windows" | "linux" | "macos")[];
}) {
  return (
    <div className="flex justify-end gap-3">
      {platforms.map((platform) => {
        const report = reports.find((r) => r.platform === platform);
        const result = getDetectionStatus(
          results?.find((r) => r.platform === platform),
        );

        const status = report ? "created" : result?.status;

        let Icon = CloseIcon;

        if (status === "created") {
          Icon = CheckmarkIcon;
        }

        if (status === "error") {
          Icon = AlertIcon;
        }

        const created = report?.created ?? result?.created;

        return (
          <TooltipProvider key={platform}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className={cn(
                    "border-stroke-line",
                    status === "created" && "border-stroke-input",
                  )}
                  icon={
                    <Icon
                      className={cn(
                        "text-secondary-foreground",
                        status === "created" && "text-status-green",
                        status === "error" && "text-status-red",
                      )}
                    />
                  }
                  intent="secondary"
                >
                  <PlatformIcon platform={platform} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {created && (
                  <div className="flex flex-col items-start gap-1">
                    <span>Submitted on</span>
                    <time>
                      {formatDate(parseDate(created), TimeFormats.DATE_SHORT)}
                    </time>
                  </div>
                )}

                {!status && "Not Submitted"}
                {status === "error" && (
                  <div className="flex flex-col items-start gap-1 break-words text-status-red">
                    <span>Error:</span>
                    {result?.error && (
                      <p className="max-w-96">{result.error.toString()}</p>
                    )}
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}

function EDRExpandedDetections({
  results,
  reports,
  platforms,
  sentMessage,
}: {
  results?: BlockResponse[];
  reports: DetectionReport[];
  platforms: ("windows" | "linux" | "macos")[];
  sentMessage: string;
}) {
  return (
    <div className="mt-4 flex flex-col border-t border-stroke-line">
      {platforms.map((platform) => {
        const report = reports.find((r) => r.platform === platform);
        const result = getDetectionStatus(
          results?.find((r) => r.platform === platform),
        );

        const status = result ? result.status : report ? "created" : undefined;

        let Icon = CloseIcon;

        if (status === "created") {
          Icon = CheckmarkIcon;
        }

        if (status === "error" || status === "already_exists") {
          Icon = AlertIcon;
        }

        const created = report?.created ?? result?.created;

        return (
          <div
            className="flex items-center gap-2 border-b border-dashed border-stroke-line py-2 last-of-type:border-none"
            key={platform}
          >
            <div className="flex w-64 items-center gap-1 text-xs">
              <Icon
                className={cn(
                  "text-secondary-foreground",
                  status === "created" && "text-status-green",
                  status === "error" && "text-status-red",
                  status === "already_exists" && "text-status-red",
                )}
              />
              <PlatformIcon
                platform={platform}
                className={cn(
                  "mr-1 text-secondary-foreground",
                  status === "created" && "text-primary-foreground",
                )}
              />
              {!status && "Not Submitted"}
              {status === "created" && "Submitted"}
              {status === "error" && (
                <div className="text-status-red">
                  <span>Error: {result?.error && result.error}</span>
                </div>
              )}
              {status === "already_exists" && (
                <div className="text-status-red">
                  <span>Error: Already Exists</span>
                </div>
              )}
            </div>
            {created && (
              <span className="text-xs">
                {displayPlatform(platform)} {sentMessage} on{" "}
                {formatDate(parseDate(created), TimeFormats.DATE_FIRST)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

const TestThreatsActivity: React.FC<{
  data: ThreatActivityEntry[];
  counts: { threats: number; results: number };
  view: "threats" | "results";
  setView: (view: "threats" | "results") => void;
}> = ({ data, counts, view, setView }) => {
  const [activeSearch, setActiveSearch] = useState<boolean>(false);
  const columnHelper = createColumnHelper<ThreatActivityEntry>();
  const columns = [
    columnHelper.accessor("threatId", threatIdColumn),
    columnHelper.accessor("source_id", sourceIdColumn),
    columnHelper.accessor("name", threatNameSmallColumn),
    columnHelper.accessor("author", authorColumn),
    columnHelper.accessor("protected", protectedColumn),
  ];

  const table = useTable({
    name: "test-threats",
    data,
    columns,
    initialState: { columnVisibility: { [threatIdColumn.id]: false } },
  });

  return (
    <section className="flex flex-col border-t border-stroke-line">
      <div className="flex h-16 items-center p-4">
        {!activeSearch && (
          <ToggleView
            threatActivityCount={counts.threats}
            resultActivityCount={counts.results}
            view={view}
            setView={setView}
          />
        )}
        {activeSearch ? (
          <TableFilterInput
            className="lg:w-full"
            table={table}
            onClose={() => setActiveSearch(false)}
          />
        ) : (
          <IconButton
            icon={<SearchIcon />}
            onClick={() => setActiveSearch(true)}
          />
        )}
      </div>

      <div className="border-b border-dashed border-stroke-line" />

      <div className="border-b border-stroke-line p-4">
        <VirtualTable className="max-h-[500px]" table={table} />
      </div>
    </section>
  );
};

const TestResultsActivity: React.FC<{
  data: TestResult[];
  counts: { threats: number; results: number };
  view: "threats" | "results";
  setView: (view: "threats" | "results") => void;
}> = ({ data, counts, view, setView }) => {
  const dropdown = usePopper();
  const [activeSearch, setActiveSearch] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const logsData = useMemo(() => {
    return (data || []).filter((log) => {
      const isPass = (ExitCodeGroup.PROTECTED as unknown as number[]).includes(
        log.status,
      );
      const isFail = (
        ExitCodeGroup.UNPROTECTED as unknown as number[]
      ).includes(log.status);
      const isError = (ExitCodeGroup.ERROR as unknown as number[]).includes(
        log.status,
      );

      if (statusFilter === "Prevented") return isPass;
      if (statusFilter === "Not prevented") return isFail;
      if (statusFilter === "Error") return isError;

      return true;
    });
  }, [data, statusFilter]);

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<TestResult>();
    return [
      columnHelper.accessor("host", hostSmallColumn),
      columnHelper.accessor("os", osColumn),
      columnHelper.accessor("date", timeColumn),
      columnHelper.accessor(
        (row) =>
          isValidControl(row.control) ? getControlName(row.control) : undefined,
        edrColumn,
      ),
      columnHelper.accessor("observed", observedResultColumn),
      columnHelper.accessor("detected", detectedResultColumn),
      columnHelper.accessor("status", preventedStatusColumn),
      columnHelper.accessor("expected_outcome", expectedOutcomeColumn),
      columnHelper.display({
        id: "actions",
        enableHiding: false,
        cell: (info) => {
          return <DeepLinkActionsColumn {...info.row.original} />;
        },
        meta: {
          widths: ["50px", "50px"],
          authorize: (account) =>
            hasFeatures(account, ["detections"]) &&
            account.controls.some((c) => c.id === ControlCodes.CROWDSTRIKE),
        },
      }),
    ];
  }, []);

  const table = useTable({
    name: "test-results",
    data: logsData,
    columns,
    initialState: {
      sorting: [{ id: timeColumn.id, desc: true }],
      columnVisibility: {
        [observedResultColumn.id]: false,
        [detectedResultColumn.id]: false,
      },
    },
  });

  const downloadReport = useDownloadFile({
    data: JSON.stringify(logsData, null, 2),
    filename: `${data[0]?.testId}-log.json`,
    type: "application/json",
  });
  const statusOptions = ["All", "Prevented", "Error", "Not prevented"];

  return (
    <section className="flex flex-col border-t border-stroke-line">
      <div className="flex h-16 items-center justify-between p-4">
        {!activeSearch && (
          <ToggleView
            threatActivityCount={counts.threats}
            resultActivityCount={counts.results}
            view={view}
            setView={setView}
          />
        )}
        <div className="flex items-center gap-4">
          {!activeSearch && data.length > 0 && (
            <Button
              intent="pill"
              icon={<DownloadIcon className="text-secondary-foreground" />}
              onClick={() => downloadReport.execute()}
            >
              Export
            </Button>
          )}
          {!activeSearch && (
            <Dropdown {...dropdown}>
              <DropdownTrigger asChild>
                <Button
                  intent="pill"
                  icon={
                    <RotatingChevron
                      className="text-secondary-foreground"
                      active={dropdown.open}
                    />
                  }
                  iconPosition="right"
                >
                  {statusFilter}
                </Button>
              </DropdownTrigger>

              <DropdownContent className="w-28">
                <DropdownRadioGroup
                  value={statusFilter}
                  onValueChange={setStatusFilter}
                >
                  {statusOptions.map((s) => (
                    <DropdownRadio key={s} value={s}>
                      {s}
                    </DropdownRadio>
                  ))}
                </DropdownRadioGroup>
              </DropdownContent>
            </Dropdown>
          )}
          {activeSearch ? (
            <TableFilterInput
              className="lg:w-full"
              table={table}
              onClose={() => setActiveSearch(false)}
            />
          ) : (
            <IconButton
              icon={<SearchIcon />}
              onClick={() => setActiveSearch(true)}
            />
          )}
        </div>
      </div>

      <div className="border-b border-dashed border-stroke-line" />

      <div className="border-b border-stroke-line p-4">
        <VirtualTable
          className="max-h-[500px]"
          table={table}
          emptyState={
            <span className="flex justify-center">
              There are no results for this filter. Please try a different one.
            </span>
          }
        />
      </div>
    </section>
  );
};

const ToggleView: React.FC<{
  threatActivityCount: number;
  resultActivityCount: number;
  view: "threats" | "results";
  setView: (view: "threats" | "results") => void;
}> = ({ threatActivityCount, resultActivityCount, view, setView }) => {
  return (
    <div className="mr-auto">
      <ToggleGroup value={view} onValueChange={setView}>
        <ToggleItem value="threats" count={threatActivityCount}>
          Threats
        </ToggleItem>
        <ToggleItem value="results" count={resultActivityCount}>
          Results
        </ToggleItem>
      </ToggleGroup>
    </div>
  );
};

function RegenerateModal({
  test,
  detections,
  threatHunts,
}: {
  test: Test;
  detections: Detection[];
  threatHunts: ThreatHunt[];
}) {
  const dialog = usePopper();

  return (
    <Dialog
      open={dialog.open}
      onOpenChange={(value) => {
        if (!value) {
          dialog.close();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          intent="secondary"
          icon={<RefreshIcon />}
          onClick={() => dialog.on()}
        >
          Regenerate
        </Button>
      </DialogTrigger>

      <DialogContent
        disableImplicitClose
        className="max-h-[700px] max-w-[800px] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Regenerate {test.name}</DialogTitle>
        </DialogHeader>
        <Suspense
          fallback={
            <div className="h-65 w-full animate-pulse rounded-md bg-secondary-foreground" />
          }
        >
          <RegenerateCode
            test={test}
            close={dialog.close}
            detections={detections}
            threatHunts={threatHunts}
          />
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}

const RegenerateCodeRunning = () => {
  const [progress, setProgress] = useState(20);
  useEffect(() => {
    const interval = setInterval(
      () =>
        setProgress((prevValue) => {
          return prevValue + 8 > 99 ? 99 : prevValue + 8;
        }),
      7500,
    );

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 pt-8">
      <ThreatImage bgNumber={6} elNumber={6} />
      <div className="flex flex-col items-center gap-4 text-base">
        <ProgressBar value={progress} />
        <span className="animate-pulse">Regenerating test...</span>
      </div>
    </div>
  );
};

function RegenerateCode({
  test,
  detections,
  threatHunts,
  close,
}: {
  test: Test;
  detections: Detection[];
  threatHunts: ThreatHunt[];
  close: () => void;
}) {
  const authConfig = useAuthConfig();
  const regenerateTechnique = useGenerateTechnique();
  const [jobId, setJobId] = useState<string | undefined>();

  const { data: readMe } = useSuspenseQuery(
    queries.detect.getReadMe(authConfig, test.id),
  );
  const { watch, register, handleSubmit } = useForm<{
    intel_context: string;
  }>({
    defaultValues: {
      intel_context: test.intel_context ?? "",
    },
  });

  const submit = (data: { intel_context: string }) => {
    if (!test.technique) {
      throw new Error(
        "This test has no technique, please update it and try again",
      );
    }
    regenerateTechnique.mutate(
      {
        technique: test.technique,
        intel_context: data.intel_context,
      },
      {
        onSuccess: ({ job_id }) => {
          setJobId(job_id);
        },
        onError: (error) =>
          notifyError({
            message: "Regeneration request failed",
            error,
            dismissible: true,
          }),
      },
    );
  };

  const intel = useQuery({
    // The 'as' type assertion is used here because this Query is always expected to be a string due to the 'enabled' property below
    ...queries.generate.getThreatIntel(authConfig, jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      if (
        query.state.data?.status === "COMPLETE" ||
        query.state.data?.status === "FAILED"
      ) {
        return false;
      }
      return 1500;
    },
  });

  const isLoading = intel.isLoading || intel.data?.status === "RUNNING";

  return (
    <DialogBody>
      {!intel.isFetching && !intel.data && (
        <form>
          <InputField label={"Provide context for this technique"} required>
            {/* There is a 500 character limit to reduce high usage of tokens from OpenAI */}
            <textarea
              {...register("intel_context", { maxLength: 500 })}
              placeholder={"Enter context for this technique"}
              spellCheck={false}
              maxLength={500}
              className="h-64 w-full resize-none rounded border border-stroke-input bg-surface-background px-3 py-2 text-sm focus:border-primary-foreground focus:outline-none"
            />
          </InputField>
          <span className="flex justify-end pt-2 text-sm text-secondary-foreground">
            {500 - watch("intel_context").length} characters left
          </span>
          <DialogFooter>
            <DialogClose asChild>
              <Button intent="secondary">Cancel</Button>
            </DialogClose>
            <Button
              intent="primary"
              loading={regenerateTechnique.isPending || intel.isFetching}
              disabled={regenerateTechnique.isPending || intel.isFetching}
              onClick={handleSubmit(submit)}
            >
              Regenerate
            </Button>
          </DialogFooter>
        </form>
      )}
      {isLoading && <RegenerateCodeRunning />}
      {intel.data?.status === "FAILED" && (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <img
            className="h-[18.75rem] w-[18.75rem]"
            src={"/images/jellyfish.png"}
          />
          <span className="text-base">
            Error occured while trying to regenerate test, please try again
          </span>
          <Button intent="secondary" onClick={() => close()}>
            Close
          </Button>
        </div>
      )}
      {intel.data?.status === "COMPLETE" &&
        intel.data.output[0]?.ai_generated && (
          <RegenerateCodeEditor
            output={intel.data.output[0].ai_generated}
            test={test}
            detections={detections}
            threatHunts={threatHunts}
            readme={readMe}
            close={() => {
              close();
              setJobId(undefined);
            }}
          />
        )}
    </DialogBody>
  );
}

function RegenerateCodeEditor({
  output,
  test,
  detections,
  threatHunts,
  readme,
  close,
}: {
  output: AiGeneratedOutput;
  test: Test;
  detections: Detection[];
  threatHunts: ThreatHunt[];
  readme: string;
  close: () => void;
}) {
  const [isCompiled, setIsCompiled] = useState<boolean>(true);
  const { handleSubmit, control } = useForm<UpdateTestForm>({
    defaultValues: {
      testEditor: {
        sourceCode: output.go_code,
        detections: formatEditableDetectionsFromAI(output.sigma_rules),
        threatHunts: formatEditableThreatHuntsFromAI(
          output.threat_hunt_queries,
        ),
        readme,
      },
    },
  });

  useBlocker(
    () =>
      window.confirm("You have unsaved edits, are you sure you want to leave?"),
    true,
  );

  const updateTestContents = useUpdateRegeneratedTest();

  async function updateTest(formValues: UpdateTestForm) {
    updateTestContents.mutate(
      {
        test,
        intel_context: output.intel_context,
        detections,
        threatHunts,
        testEditor: formValues.testEditor,
      },
      {
        onSuccess: async () => {
          notifySuccess({
            message: "Successfully updated test",
          });
          close();
        },
      },
    );
  }

  return (
    <form
      className="flex flex-col gap-4 p-4"
      onSubmit={handleSubmit(updateTest)}
    >
      <Controller
        name="testEditor"
        control={control}
        render={({ field }) => (
          <TestEditorField
            state={field.value}
            onChange={field.onChange}
            isCompiled={isCompiled}
            setIsCompiled={setIsCompiled}
          />
        )}
      />
      <div className="flex items-center gap-4">
        <Button type="button" intent="secondary" onClick={close}>
          Cancel
        </Button>
        <Button
          type="submit"
          intent="primary"
          disabled={updateTestContents.isPending || !isCompiled}
          loading={updateTestContents.isPending}
        >
          Save
        </Button>
      </div>
    </form>
  );
}

function LoadingCode() {
  return (
    <div className="flex px-4">
      <div className="h-[300px] w-full animate-pulse rounded-md bg-secondary-foreground" />
    </div>
  );
}

function TestSourceCode({ testId }: { testId: string }) {
  const authConfig = useAuthConfig();
  const { data: sourceCode } = useSuspenseQuery(
    queries.detect.getSourceCode(authConfig, testId),
  );

  return (
    <div className="flex flex-col gap-4 px-4">
      <Editor value={sourceCode} filename={`${testId}.go`} type="go" />
    </div>
  );
}

function LoadingReadme() {
  return (
    <div className="flex h-full w-full flex-col gap-6 pt-4">
      <div className="space-y-2 pl-4">
        <div className="h-4 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="h-4 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="h-4 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="h-4 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="h-4 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
      </div>
    </div>
  );
}

function Readme({ testId }: { testId: string }) {
  const authConfig = useAuthConfig();
  const { data: readMe } = useSuspenseQuery(
    queries.detect.getReadMe(authConfig, testId),
  );

  return (
    <div className={styles.readme}>
      <ReactMarkdown>{readMe}</ReactMarkdown>
    </div>
  );
}

function LoadingEDR() {
  return (
    <div className="h-9 w-1/3 animate-pulse rounded-md bg-secondary-foreground" />
  );
}

function ViewEDR({
  test,
  detections,
  controlCode,
  emptyMessage,
  sendButtonText,
  sentMessage,
}: {
  controlCode: ControlCode;
  test: Test;
  detections: Detection[];
  emptyMessage: string;
  sendButtonText: string;
  sentMessage: string;
}) {
  const authConfig = useAuthConfig();
  const reports = useSuspenseQuery(
    queries.partner.reports(authConfig, controlCode, test.id),
  ).data;
  const blockMutation = usePartnerBlockMutationWithToast();
  const collapsible = useCollapsible();

  const resultPlatforms =
    blockMutation.data?.map((result) => result.platform) ?? [];
  const reportPlatforms = reports?.map((report) => report.platform) ?? [];

  const supportedPlatforms = platforms.filter(
    (platform) =>
      resultPlatforms.includes(platform) || reportPlatforms.includes(platform),
  );

  const hasDetections = detections.length > 0;
  const showCollapsible = hasDetections && supportedPlatforms.length > 0;

  return (
    <>
      <Collapsible {...collapsible}>
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex w-64 shrink-0 items-center gap-1">
            {showCollapsible && (
              <CollapsibleTrigger>
                <RotatingChevron
                  active={collapsible.open}
                  className="text-secondary-foreground"
                />
              </CollapsibleTrigger>
            )}
            <div className="flex items-center gap-2">
              <ControlIcon
                className="text-secondary-foreground"
                control={controlCode}
              />
              {getControlName(controlCode)}
            </div>
          </div>
          <div className="flex w-full items-center justify-between">
            {hasDetections ? (
              <>
                <EDRDetections
                  results={blockMutation.data}
                  reports={reports}
                  platforms={supportedPlatforms}
                />
                <Button
                  loading={blockMutation.isPending}
                  onClick={() =>
                    blockMutation.mutate({
                      controlCode,
                      testId: test.id,
                    })
                  }
                  icon={<DetectionIcon />}
                  intent="secondary"
                >
                  {sendButtonText}
                </Button>
              </>
            ) : (
              <span className="text-secondary-foreground">{emptyMessage}</span>
            )}
          </div>
        </div>
        <CollapsibleContent>
          <EDRExpandedDetections
            results={blockMutation.data}
            reports={reports}
            platforms={supportedPlatforms}
            sentMessage={sentMessage}
          />
        </CollapsibleContent>
      </Collapsible>
    </>
  );
}

const ProtectedTestPercent: React.FC<{
  expected: PartnerExpected;
  activity: TestsActivity | undefined;
}> = ({ expected, activity }) => {
  const expectation = getExpectedName(expected.crowdstrike);
  const percent = getExpectedPercent(activity?.personal, expectation);
  const textColor = getStatusColor(percent);
  const borderColor = getStatusBorderColor(percent);

  if (!expectation && !percent) {
    return <NonApplicablePercent />;
  }

  return (
    <div className={cn("flex flex-col gap-2", textColor)}>
      {expectation && <span className="text-xs">Expected</span>}
      <div
        className={cn(
          "flex w-max flex-col border border-stroke-line p-4",
          borderColor,
        )}
      >
        <div className="flex items-center gap-1">
          {expectation ? (
            <>
              <ResponseIcon expectedResponse={expected.crowdstrike} />
              <span className="text-sm font-medium">
                {capitalize(expectation)}
              </span>
            </>
          ) : (
            <span className="text-sm font-medium">Protected</span>
          )}
        </div>
        <span className="text-4xl">{percent ?? 0}%</span>
        <Flag features={["observed_detected"]}>
          {(expectation === "observed" || expectation === "detected") && (
            <span className="text-xs">By CrowdStrike</span>
          )}
        </Flag>
      </div>
    </div>
  );
};

function TestDetails({
  test,
  detections,
  threatHunts,
}: {
  test: AttachedTest;
  detections: Detection[];
  threatHunts: ThreatHunt[];
}) {
  const tabs = useMemo(() => getTabs(test), [test]);
  const [activeTab, setActiveTab] = useState<string | undefined>(tabs[0]);

  const orderedThreatHunts = useMemo(() => {
    return threatHunts.sort((a, b) => a.name.localeCompare(b.name));
  }, [threatHunts]);
  return (
    <>
      <div className="flex flex-col p-4">
        <ToggleGroup value={activeTab} onValueChange={setActiveTab}>
          {tabs.includes("description") && (
            <ToggleItem value="description">
              <div className="flex items-center gap-2">
                <InfoIcon className="size-4" />
                Description
              </div>
            </ToggleItem>
          )}

          {tabs.includes("sourceCode") && (
            <ToggleItem value="sourceCode">
              <div className="flex items-center gap-2">
                <CodeIcon className="size-4" />
                Source Code
              </div>
            </ToggleItem>
          )}

          <Flag features={["detections"]}>
            {detections.length > 0 && (
              <ToggleItem value="customDetection">
                <div className="flex items-center gap-2">
                  <SignalIcon className="size-4" />
                  Custom Detection
                </div>
              </ToggleItem>
            )}
          </Flag>

          <Flag features={["threat_intel"]}>
            {threatHunts.length > 0 && (
              <ToggleItem value="threatHunt">
                <div className="flex items-center gap-2">
                  <TargetIcon className="size-4" />
                  Threat Hunting
                </div>
              </ToggleItem>
            )}
          </Flag>
        </ToggleGroup>
      </div>

      <div className="min-h-[300px] pb-10">
        {activeTab === "description" && (
          <Suspense fallback={<LoadingReadme />}>
            <Readme testId={test.id} />
          </Suspense>
        )}

        {activeTab === "sourceCode" && (
          <Suspense fallback={<LoadingCode />}>
            <TestSourceCode testId={test.id} />
          </Suspense>
        )}

        <Flag features={["detections"]}>
          {activeTab === "customDetection" && (
            <ViewDetections detections={detections} />
          )}
        </Flag>

        <Flag features={["threat_intel"]}>
          {activeTab === "threatHunt" && (
            <ViewThreatHunts threatHunts={orderedThreatHunts} />
          )}
        </Flag>
      </div>
    </>
  );
}

export default function TestView({ props }: { props: TestOverlayData }) {
  const [view, setView] = useState<"threats" | "results">("threats");
  const overlay = useOverlay();
  const { data: account } = useAccountQuery();
  const isAdmin = isAdminUser(account) ?? false;
  const {
    test,
    testActivity,
    threatActivity,
    results,
    accountControls,
    detections,
    threatHunts,
  } = props;

  const counts = {
    threats: threatActivity?.length ?? 0,
    results: results?.length ?? 0,
  };

  return (
    <div>
      <div>
        <div className="mx-4 flex items-center gap-6 border-b border-stroke-line py-4">
          {test.technique && (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-secondary-foreground">
                  MITRE ATT&CK Technique ID
                </span>
                <span className="flex items-center gap-1 text-base">
                  {test.technique}
                  <ExternalLink
                    href={`https://attack.mitre.org/techniques/${test.technique?.replaceAll(".", "/")}`}
                  />
                </span>
              </div>
              <div className="h-[44px] border-r border-stroke-line" />
            </>
          )}

          {test.created && (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-secondary-foreground">
                  Created
                </span>
                <span className="text-base">
                  {formatDate(new Date(test.created), TimeFormats.DATE_LONG)}
                </span>
              </div>
              <div className="h-[44px] border-r border-stroke-line" />
            </>
          )}

          {test.supported_platforms.length > 0 && (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-secondary-foreground">
                  Platforms
                </span>
                <span className="flex items-center gap-2">
                  {test.supported_platforms.map((platform) => (
                    <PlatformIcon
                      className="size-4"
                      platform={platform}
                      key={platform}
                    />
                  ))}
                </span>
              </div>
              <div className="h-[44px] border-r border-stroke-line" />
            </>
          )}

          {test.author && (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-secondary-foreground">Author</span>
              <span className="flex items-center gap-1 text-base">
                <UserIcon className="text-secondary-foreground" />
                <span className="max-w-40 truncate pr-1">{test.author}</span>
                {test.account_id === "prelude" && <VSTIndicatorIcon />}
              </span>
            </div>
          )}
        </div>

        <section className="flex flex-1 flex-col gap-4 p-4">
          <h4 className="text-3xl leading-9">{test.name}</h4>
          <OverlayInfoBlock title="Test ID">
            <div className="flex items-center gap-1">
              {test.id}
              <CopyButton toCopy={test.id} />
            </div>
          </OverlayInfoBlock>

          <div className="flex items-center gap-6">
            <Flag
              features={["observed_detected"]}
              fallback={<ProtectedPercent percent={testActivity?.protected} />}
            >
              <ProtectedTestPercent
                expected={test.expected}
                activity={testActivity}
              />
            </Flag>

            {test.author === "Prelude" && (
              <BenchmarkPercent social={testActivity?.social} />
            )}
          </div>

          {testActivity && (
            <Flag features={["observed_detected"]}>
              <div className="flex items-center gap-4 text-sm text-secondary-foreground">
                <div className="flex items-center gap-1">
                  <EyeIcon className="size-4" />
                  <span>Observed</span>
                  <span className="pl-1 text-primary-foreground font-medium">
                    {formatPercent(
                      testActivity.personal.observed,
                      testActivity.personal.partner_total,
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <SignalIcon className="size-4" />
                  <span>Detected</span>
                  <span className="pl-1 text-primary-foreground font-medium">
                    {formatPercent(
                      testActivity.personal.detected,
                      testActivity.personal.partner_total,
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <ShieldFlash className="size-4" />
                  <span>Prevented</span>
                  <span className="pl-1 text-primary-foreground font-medium">
                    {formatPercent(
                      testActivity.personal.prevented,
                      testActivity.personal.prelude_total,
                    )}
                  </span>
                </div>
              </div>
            </Flag>
          )}

          <div className="flex items-center gap-4">
            <Flag features={["threat_intel"]}>
              {test.account_id === account?.account_id && isAdmin && (
                <RegenerateModal
                  test={test}
                  detections={detections}
                  threatHunts={threatHunts}
                />
              )}
            </Flag>
            <Button
              icon={<CalendarIcon />}
              intent="primary"
              className="w-max"
              onClick={() =>
                overlay.open("schedule", {
                  type: "test",
                  defaultSelectedId: test.id,
                })
              }
            >
              Schedule
            </Button>
          </div>

          {accountControls.length > 0 && (
            <Flag features={["detections"]}>
              <OverlayInfoBlock title="Endpoint Detection & Response (EDR)">
                <div className="mt-1 flex flex-col gap-2">
                  {accountControls.includes(ControlCodes.CROWDSTRIKE) && (
                    <Suspense fallback={<LoadingEDR />}>
                      <ViewEDR
                        controlCode={ControlCodes.CROWDSTRIKE}
                        detections={detections}
                        test={test}
                        emptyMessage="No IOA available"
                        sendButtonText="Send IOA"
                        sentMessage="IOA sent to CrowdStrike"
                      />
                    </Suspense>
                  )}

                  {accountControls.includes(ControlCodes.DEFENDER) && (
                    <Suspense fallback={<LoadingEDR />}>
                      <ViewEDR
                        controlCode={ControlCodes.DEFENDER}
                        detections={detections}
                        test={test}
                        emptyMessage="No detections available"
                        sendButtonText="Send detections"
                        sentMessage="Detections sent to Defender"
                      />
                    </Suspense>
                  )}
                </div>
              </OverlayInfoBlock>
            </Flag>
          )}
        </section>

        {view === "results" && (
          <TestResultsActivity
            data={results ?? []}
            counts={counts}
            view={view}
            setView={setView}
          />
        )}

        {view === "threats" && (
          <TestThreatsActivity
            data={threatActivity ?? []}
            counts={counts}
            view={view}
            setView={setView}
          />
        )}
      </div>

      <TestDetails
        test={test}
        detections={detections}
        threatHunts={threatHunts}
      />
    </div>
  );
}
